import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { useContext } from 'react'

import { PreviewContext } from 'contexts/previewContext'
import { createApiCall } from 'services'
import type { MyPendingSalesResponse } from 'types'

export const MY_PENDING_SALES_QUERY_KEY = ['pending-sales', 'mine'] as const

const getMyPendingSales = async (isPreview: boolean): Promise<MyPendingSalesResponse> => {
  // Studio preview renders a fabricated certificate list and has no session, so
  // an authenticated call here would 401 on every preview load. Empty is the
  // honest answer: a preview brand has recorded no sales to anyone.
  if (isPreview) return { items: [] }

  const response = await createApiCall({ method: 'GET', url: '/pending-ownership/customer' })

  const status = (response as { status?: number } | undefined)?.status

  /*
   * A FAILED FETCH IS AN EMPTY LIST, not an error the page surfaces.
   *
   * Deliberately unlike `useGetCertificates`, which throws so the page can tell
   * a 401 from a genuinely empty account. The difference is what the failure
   * costs: certificates ARE the page, so silently showing none is a lie the
   * user acts on. These rows are supplementary — an item that has not been
   * claimed yet. If this call fails, the certificates the buyer already owns
   * must still render, and an error state stacked above them would report a
   * problem with a feature most accounts never touch.
   *
   * The trade is real and is accepted with eyes open: a buyer whose pending row
   * fails to load sees nothing rather than an explanation. The mitigation is
   * that the same failure almost certainly took the certificates query with it,
   * and that one does explain itself.
   */
  if (typeof status !== 'number' || status < 200 || status >= 300) {
    return { items: [] }
  }

  return (response.data ?? { items: [] }) as MyPendingSalesResponse
}

/**
 * VRG-455 — the buyer's recorded sales that are still waiting on their scan.
 *
 * Polled on the same 20s cadence as the certificates list (`useGetCertificates`)
 * and for the same reason: there is no push channel, and the moment that matters
 * is someone sitting on the list with the app open. Keeping the two intervals
 * equal also stops the pending rows and the certificate cards updating on
 * visibly different beats, which reads as one of them being stale.
 */
export const useMyPendingSales = (): UseQueryResult<MyPendingSalesResponse, Error> => {
  const { isPreview } = useContext(PreviewContext)

  return useQuery({
    queryKey: MY_PENDING_SALES_QUERY_KEY,
    queryFn: () => getMyPendingSales(isPreview),
    retry: false,
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
  })
}
